/**
 * Keep the selected item of a horizontally scrolling row on screen.
 *
 * **The bug this exists for** (owner, 2026-09-21): on a phone the project tab row scrolls, and
 * every navigation re-mounted it at `scrollLeft: 0` — so a person standing on *Settings*, the
 * ninth tab, saw *Overview · Documentation · Register* and no mark anywhere. The row reported
 * the one thing it exists to report — where you are — only when where you are happened to be
 * first. The same is true of any scrolling row with a selected item in it, which is why this is
 * one object and not a `useEffect` in the tab row (retro rule 8).
 *
 * **Centred, and clamped by the browser.** The item is pulled to the middle of the row, except
 * near the ends: assigning past the scroll range is clamped, so the first items sit at the
 * start and the last at the end with no arithmetic here and no special case to get wrong.
 *
 * **Not `Element.scrollIntoView`.** That one walks *every* scrollable ancestor — the pane, the
 * page — so centring a tab inside a 44px row also scrolls the document. This writes `scrollLeft`
 * on the one element it was handed and touches nothing else.
 *
 * The first placement is instant: a row that animates itself into position on load is a row
 * that looks like it moved while you were reading it. Later ones glide, because then the
 * person pressed something and the movement is the feedback — unless they asked for no motion.
 */

import { useLayoutEffect, useRef } from "react";

import type { RefObject } from "react";

export interface ScrollRow<
  Scroller extends HTMLElement,
  Item extends HTMLElement,
> {
  /** The element with `overflow-x: auto`. */
  readonly scrollerRef: RefObject<Scroller | null>;
  /** Put this on the selected item only — it is what gets centred. */
  readonly selectedRef: RefObject<Item | null>;
}

/**
 * @param selected Whatever identifies the current item. The row re-centres when it changes,
 *   so a row whose selection never changes places itself once and then holds still.
 */
export function useCenterSelected<
  Scroller extends HTMLElement = HTMLElement,
  Item extends HTMLElement = HTMLElement,
>(selected: string | null): ScrollRow<Scroller, Item> {
  const scrollerRef = useRef<Scroller | null>(null);
  const selectedRef = useRef<Item | null>(null);
  const placed = useRef(false);

  useLayoutEffect(() => {
    const scroller = scrollerRef.current;
    const item = selectedRef.current;
    if (scroller === null || item === null) return;

    const row = scroller.getBoundingClientRect();
    const box = item.getBoundingClientRect();
    const delta = box.left + box.width / 2 - (row.left + row.width / 2);
    // Sub-pixel: the row is already where it should be, and a `scrollTo` for half a pixel is a
    // smooth animation of nothing.
    if (Math.abs(delta) < 1) return;

    const instant =
      !placed.current ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    placed.current = true;
    scroller.scrollTo({
      left: scroller.scrollLeft + delta,
      behavior: instant ? "auto" : "smooth",
    });
  }, [selected]);

  return { scrollerRef, selectedRef };
}
